/**
 * The partner port (DISCOUNT_V2_PLAN.md §9.12, decision 25; R2.4c.2, 2026-10-12): the whole partner
 * conversation over the declared routes, in the domain's words.
 *
 * 🔴 **Domain types in, domain outcomes out; no backend shape crosses this file.** A source asks
 * `listOffers`, `checkCode`, `requestApply`, `verify` or `release` and gets a named outcome back
 * (`listed`, `found`, `needs-otp`, `verified`, `released`, `failed` with the backend's code …). How
 * each partner's request is built and its answer read is the partner's ADAPTER: its data and its
 * steps (`./steps.ts`), picked by `getPartner`. The routes' envelopes (what the backend wraps around
 * a partner's answer) are read here, once: docs/architecture/discounts/partner-answers.md §1.
 *
 * 🔴 **Whose rules (decision 37): the offer's own partner**, its `couponProvider`, else the active
 * offers provider as v1 (Reelo, Fudr and Exclusife stamp none). The provider id sent is that
 * partner's. v1 read every answer with the active provider's rules.
 *
 * 🔴 **The reference id, the code and the request id a release sends are the ENTRY's own (decisions
 * 36, 38, 40)**, never a global slot: each coupon on the bill carries its own.
 *
 * The network is the app's one client (`apiClient()`, API_ACCESS.md), which refuses a route the
 * session may not call without sending it; a test hands in its own `call` over a recording transport
 * (`partners/contract/contract.test.ts` runs every partner's recorded answers through here).
 *
 * ```
 *   listOffers(sale) ─▶ POST /sale/customer/offers        ─▶ listed (each offer with its partner and key) | refused
 *   checkCode(code)  ─▶ POST /coupon/verify               ─▶ found (the coupon) | already-applied | refused
 *   requestApply(o)  ─▶ POST /sale/customer/loyalty/redeem ─▶ needs-otp | apply-now | failed
 *   verify(o, otp)   ─▶ the partner's verify channel       ─▶ verified | failed
 *   release(entry)   ─▶ POST /sale/customer/offer/unblock  ─▶ released | skipped (a field it needs is missing) | failed
 * ```
 *
 * The POINTS steps (R2.4c.3a, docs/architecture/loyalty/points-conversations.md §1, §2) are beside them,
 * on the loyalty routes:
 *
 * ```
 *   pointsBalance(sale)   ─▶ POST /sale/customer/loyalty         ─▶ balance | unanswered | consent-required | refused
 *   requestRedeem(points) ─▶ POST /sale/customer/loyalty/redeem  ─▶ needs-otp | answered-without-otp | failed
 *   verifyRedeem(otp)     ─▶ POST /sale/customer/loyalty/verify  ─▶ verified | failed | consent-required
 *   refundPoints(sale)    ─▶ POST /sale/customer/loyalty/refund  ─▶ refunded | not-refunded | failed
 *   releaseHold(hold)     ─▶ POST /sale/customer/loyalty/release ─▶ released | not-needed | failed
 * ```
 *
 * The CONSENT and PROGRAMME steps (R2.4c.3b) and a typed code's OTP (decision 69) follow:
 *
 * ```
 *   sendConsentOtp(c)  ─▶ POST /customer/consent-otp          ─▶ otp-sent | already-consented | enrolled-without-otp | failed
 *   verifyConsent(c)   ─▶ POST /customer/consent-otp/verify   ─▶ verified | failed | refused
 *   optOut(c)          ─▶ POST /customer/optout (DotPe only)  ─▶ not-offered | opted-out | failed
 *   programme(channel) ─▶ GET  /program/fetch                 ─▶ programme | none | refused
 *   redeemCode(code)   ─▶ POST /coupon/redeem                 ─▶ redeemed | wrong-otp | failed
 *   resendCodeOtp(code)─▶ POST /coupon/resend                 ─▶ sent | failed
 * ```
 */
import { COUPON, CUSTOMER, PROGRAM, SALE } from "../../api/routes";
import type { Route } from "../../api/route";
import type { ApiClient, ApiError, ApiResult } from "../../api/types";
import { listedCode, partnerOfferKey, type OfferKey } from "../identity";
import type { BillDiscount, DiscountOffer, Sale } from "../types";
import { PARTNERS, getPartner } from "./index";
import type { ApplyPlan, VerifyOutcome } from "./steps";
import type { Partner } from "./types";

/* ── who the merchant's partners are ───────────────────────────────────────────────────────── */

/** A partner the business has configured (`businessData.loyaltyProviders`): its provider document id and `metaId`. */
export interface ProviderRef {
  mid?: string;
  metaId?: string;
  /**
   * The provider's `meta` (the marketing provider's document): `discountCode` is the code the backend
   * writes the points line under (`sale_loyalty_service.js` 346-367), which `releaseHold` must send.
   */
  meta?: { discountCode?: string };
}

/** The merchant's partners, as one call needs them. */
export interface PartnerDirectory {
  providers: readonly ProviderRef[];
  /** The active offers provider: named on the offers request, and the partner of an offer that names none (decision 37). */
  active?: ProviderRef;
}

/** The partner an offer or an entry belongs to: its own `couponProvider`, else the active provider (decision 37). */
export const partnerOf = (discount: { couponProvider?: string } | null | undefined, directory: PartnerDirectory): string | undefined =>
  discount?.couponProvider || directory.active?.metaId;

/** The provider document id a request names for this partner; the active provider's when the partner is not configured, as v1. */
export const providerIdOf = (metaId: string | undefined, directory: PartnerDirectory): string | undefined =>
  directory.providers.find((provider) => provider.metaId === metaId)?.mid ?? directory.active?.mid;

/* ── the outcomes ──────────────────────────────────────────────────────────────────────────── */

/**
 * Why a call has no answer to read: the session may not call the route (the client refused it, or
 * the server did), there is no network, the server failed, or the session ended.
 */
export type Unanswered = "not-allowed" | "offline" | "unreachable" | "signed-out";

/** What a refusal says: the backend's or the partner's code and words, or why nothing was answered. */
export interface Refusal {
  /** `REST_INVALID_COUPON`, `REST_XENO_REQUEST_UNBLOCK_COUPON_ERROR`, …: the backend maps a partner's refusal to a code. */
  code?: string;
  /** The partner's own words, when the answer carries a sentence. */
  message?: string;
  unanswered?: Unanswered;
}

/** An offer a partner listed, with the partner whose rules it follows and its identity. */
export interface ListedOffer {
  /** The offer as the partner sent it. */
  offer: DiscountOffer;
  /** Its partner (decision 37); `undefined` only when the offer names none and no provider is active. */
  partner: string | undefined;
  /** Its identity (`identity.ts` › `partnerOfferKey`): what a press, the applied check and a release compare. */
  key: OfferKey | null;
}

export type OffersOutcome = { kind: "listed"; offers: ListedOffer[] } | ({ kind: "refused" } & Refusal);

/** A typed code the backend accepted: the coupon and the merchant's discounts it releases. */
export interface CheckedCoupon {
  /** The code as the backend answers it (it upper-cases what was typed). */
  code: string;
  /** Who issued it (`sourceInfo.provider`): a partner's `metaId`, or the merchant's own coupon provider's id. The entry's `couponProvider`. */
  issuer?: string;
  /** The partner whose rules apply, or `null` for a coupon of the merchant's own. */
  partner: string | null;
  /** The merchant's discount documents the code releases, each with its `discountId` (the backend's `_id`). */
  discounts: DiscountOffer[];
  /** The partner's correlation ids, when it issued any (EasyRewardz, Xeno). */
  requestId?: string;
  referenceId?: string;
  /** The partner sent the customer an OTP while checking (EasyRewardz, Xeno). */
  otpRequired: boolean;
  /** The coupon as the backend answered it: read only by the entry's typed-coupon keep list (`entry.ts`). */
  answer: Record<string, unknown>;
}

export type CodeOutcome =
  | { kind: "found"; coupon: CheckedCoupon }
  /** The backend says this customer's coupon is already on the bill (Xeno). */
  | { kind: "already-applied" }
  /** `status` is the coupon's own when the backend found it but it is not `Active`. */
  | ({ kind: "refused"; status?: string } & Refusal);

/** Said on every outcome a partner answered: whose rules read it, and whether the customer's loyalty record went stale. */
interface Answered {
  partner: string | undefined;
  /** The partner changed the customer's balance or milestones with this call: fetch their loyalty record again. */
  customerRecordStale: boolean;
}

export type ApplyOutcome =
  | (Extract<ApplyPlan, { kind: "needs-otp" | "apply-now" }> & Answered)
  /** `sale`: the apply route saves a sale that has no `version` before it asks the partner, and answers it even on a refusal. */
  | ({ kind: "failed"; partner: string | undefined; sale?: Sale } & Refusal);

export type VerifyResult =
  | (Extract<VerifyOutcome, { kind: "verified" }> & Answered)
  | ({ kind: "failed"; partner: string | undefined } & Refusal);

export type ReleaseOutcome =
  | ({ kind: "released" } & Answered)
  /** Not sent: the partner needs a field the entry or the sale lacks, and would refuse a call without it. */
  | { kind: "skipped"; partner: string | undefined; missing: "customerPhone" | "other" }
  | ({ kind: "failed"; partner: string | undefined } & Refusal);

/* ── the points outcomes ──────────────────────────────────────────────────────────────────── */

/**
 * What a partner's balance answer says beyond its figures. The figures are passed through as numbers,
 * never interpreted (the view decides what a minimum or a negative figure means).
 */
export type BalanceNote =
  /** Only a `minimumAmount` came: the bill is below the partner's minimum for a redemption (Reelo, DotPe), no figures. The figures here are 0, and absent. */
  | "minimum-amount-only"
  /** A figure is negative: Reelo answers `-1` for all three when redemption is not allowed today. Passed through as it is. */
  | "negative-figures";

export type BalanceOutcome =
  | {
      kind: "balance";
      redeemablePoints: number;
      redeemableAmount: number;
      currentPoints?: number;
      loyaltyTier?: string;
      /** The customer's opt-in at the partner (`loyaltyOptIn`), when it says. */
      optedIn?: boolean;
      minimumAmount?: number;
      note?: BalanceNote;
    }
  /**
   * The partner answered nothing readable (an empty body, no figure in it): decision 52, the session says
   * "couldn't read" with Retry. Never a zero balance: a partner with no phone, no customer, an error it
   * logged, or a customer whose points cannot be redeemed on this bill (eWards) all answer nothing.
   */
  | { kind: "unanswered" }
  /** `REST_RESTRICTED_ACTION`: the customer declined this partner's consent. */
  | { kind: "consent-required" }
  | ({ kind: "refused" } & Refusal);

/** What a redeem request answered, for the OTP prompt and the verify that follows. */
interface RedeemRequested {
  /** The sale the route saved before asking the partner (it has a bill number now); not changed by the partner. */
  sale: Sale;
  /** The partner's hold on the points, when it answered one (DotPe's `redemptionInfo.blockId`): what a Cancel releases. */
  blockId?: string;
  /** The winning partner's `metaId` and provider document id: the backend tried every provider (decision 37's spirit, L1b D17). */
  partner?: string;
  providerId?: string;
  /** Figures the partner priced the request with, when it answered any (Xeno, EasyRewardz): the figures are the partner's, not the cashier's. */
  redeemablePoints?: number;
  redeemableAmount?: number;
  maxPointsToRedeem?: number;
}

export type RedeemOutcome =
  /** `redemptionInfo.authenticate` is true: the partner sent the customer an OTP. */
  | ({ kind: "needs-otp" } & RedeemRequested)
  /**
   * The partner answered with no `authenticate` (mLoyal). The backend writes the points line only at
   * verify, so nothing is on the bill: the caller decides, and a verify with no OTP is the only way to put the line there.
   */
  | ({ kind: "answered-without-otp" } & RedeemRequested)
  /** A `code`, an `errorCode` or no sale is a failure (decision 50). `sale`: the route saved it before asking, and answers it on a refusal. */
  | ({ kind: "failed"; sale?: Sale } & Refusal);

export type PointsVerifyOutcome =
  /** `sale` carries the points line the backend wrote (points-conversations.md §3). */
  | ({ kind: "verified"; sale: Sale } & Answered)
  | ({ kind: "failed" } & Refusal)
  /** The customer declined this partner's consent. */
  | { kind: "consent-required" };

export type RefundOutcome =
  | ({ kind: "refunded"; sale?: Sale; refundedPoints?: number } & Answered)
  /**
   * The backend answered `refundSuccess` false OR absent: the partner said no, errored, has no refund step
   * (eWards, mLoyal), or had nothing to answer (F7). `code` is the route's `errorCode` when it gave one.
   */
  | { kind: "not-refunded"; sale?: Sale; code?: string }
  | ({ kind: "failed" } & Refusal);

export type HoldReleaseOutcome =
  | ({ kind: "released" } & Answered)
  /** Nothing to release: the partner does not hold points at request time (`holdsPointsOnRequest`), or there is no hold to name. Nothing sent. */
  | { kind: "not-needed" }
  | ({ kind: "failed" } & Refusal);

/* ── the consent, programme and typed-code outcomes ──────────────────────────────────────── */

/**
 * What the consent OTP request answered (`/customer/consent-otp`). `failed` carries the partner's own words
 * when it answered some (`Invalid Provider` for a provider that is none), and the backend's code or why
 * nothing was answered otherwise.
 */
export type ConsentOtpOutcome =
  /** `status` true: the partner sent the customer a code (`otpSent` is true, or the backend's default). */
  | { kind: "otp-sent"; isNewEnrollment?: boolean }
  /** `CUSTOMER_ALREADY_CONSENTED` (the backend found the customer opted in), or v1's `Customer Already Exists`: stop asking. */
  | { kind: "already-consented"; customerRecordStale: true }
  /** `status` true and `otpSent` false: the partner enrolled the customer outright, no code was sent. */
  | { kind: "enrolled-without-otp"; customerRecordStale: true; isNewEnrollment?: boolean }
  | ({ kind: "failed" } & Refusal);

export type ConsentVerifyOutcome =
  /** `status` true. The customer's loyalty record is stale: v1 reads it again at every terminal consent outcome. */
  | { kind: "verified"; alreadyConsented: boolean; customerRecordStale: true }
  /** The partner answered `status` false or nothing readable: the code was wrong, or the partner failed. */
  | ({ kind: "failed" } & Refusal)
  /** The backend refused the request itself (a missing `firstName` is `REST_ERR_INVALID_INPUT`), or nothing was answered. */
  | ({ kind: "refused" } & Refusal);

export type OptOutOutcome =
  /** The partner has no opt-out (decision 54): nothing was sent, and the view must not offer it. */
  | { kind: "not-offered" }
  | { kind: "opted-out"; partner: string | undefined; customerRecordStale: boolean }
  | ({ kind: "failed" } & Refusal);

export type ProgrammeOutcome =
  /** The programme, as DotPe answered it (`status` true). */
  | { kind: "programme"; programme: Record<string, unknown> }
  /** The channel has no active programme (`status` false or absent); a `profileConfig` can come with it, and the profile card reads it. */
  | { kind: "none"; profileConfig?: unknown }
  /** Nothing readable: `ERR_INVALID_BRAND` (the channel has no brand), no network, no permission. A cached programme is kept. */
  | ({ kind: "refused" } & Refusal);

export type CodeRedeemOutcome =
  /** The partner took the code. `confirmed`: it answered `status: true`, which the old FE read as the partner having verified the customer. */
  | { kind: "redeemed"; partner: string; confirmed: boolean }
  /**
   * The partner did not take the OTP: it answered `status: false` or an `error`, or the backend answered the
   * partner's refusal as a code (Exclusife's wrong OTP is only that, `REST_EXCLUSIFE_COUPONREDEEM_ERROR`). Ask
   * the customer again. `release`: the partner's `releasesCouponOnWrongOtp` (EasyRewardz) says the coupon is
   * blocked, so release it first (`release`), then check the code again (`checkCode`, which sends a fresh
   * OTP). `code` says what the backend answered, for a refusal that is no wrong OTP.
   */
  | { kind: "wrong-otp"; partner: string; release: boolean; code?: string; message?: string }
  /** Nothing was answered (offline, not allowed, unreachable, signed out), or the backend's feature check refused. */
  | ({ kind: "failed"; partner: string } & Refusal);

export type CodeResendOutcome = { kind: "sent" } | ({ kind: "failed" } & Refusal);

/* ── the port ──────────────────────────────────────────────────────────────────────────────── */

export interface PartnerPort {
  /** Every route the port may call (API_ACCESS.md §3.5); a test holds it to what it really calls. */
  readonly routes: readonly Route<any, any, any>[];
  /** The offers every partner of the sale's channel lists for its customer (the backend asks each in turn). */
  listOffers(input: { sale: Sale; directory: PartnerDirectory }): Promise<OffersOutcome>;
  /**
   * A typed code: the partners that take codes, then the merchant's own coupons (the backend tries each).
   * `branchId` is the ORDER's branch (decision 7; a call-centre session must send it).
   */
  checkCode(input: { code: string; sale: Sale; customerPhone?: string; branchId?: string; ristaDiscount?: boolean }): Promise<CodeOutcome>;
  /**
   * Ask the offer's partner to apply it. `code` is the code the cashier typed when the offer came from a
   * code check: the partner applies the customer's own instance of the coupon, not the campaign's.
   */
  requestApply(input: { offer: DiscountOffer; sale: Sale; directory: PartnerDirectory; code?: string }): Promise<ApplyOutcome>;
  /** The OTP the partner sent; `referenceId` is the one the apply answered (the session holds it, decision 36). */
  verify(input: { offer: DiscountOffer; otp: string; sale: Sale; directory: PartnerDirectory; referenceId?: string; code?: string }): Promise<VerifyResult>;
  /** Release a partner coupon that comes off the bill, with the entry's own reference id and code (decisions 36, 38). */
  release(input: { entry: BillDiscount; sale: Sale; directory: PartnerDirectory }): Promise<ReleaseOutcome>;

  /* ── points ─────────────────────────────────────────────────────────────────────────────── */

  /**
   * The customer's points at one partner (`providerId` is that partner's provider document id; the
   * backend answers only for the one named). `fetch: "redeemable"` makes DotPe price the cart.
   */
  pointsBalance(input: { sale: Sale; providerId: string; metaId?: string; fetch?: string }): Promise<BalanceOutcome>;
  /**
   * Ask the partner to redeem `points` worth `amount` on the bill (the backend tries every loyalty
   * provider of the branch in turn and the first answer wins; `directory` says which partner a
   * provider id belongs to, for an answer that names none). The points line is NOT on the sale yet:
   * the backend writes it at verify alone (points-conversations.md §3).
   */
  requestRedeem(input: {
    sale: Sale;
    points: number;
    amount: number;
    directory: PartnerDirectory;
    /** The provider the cashier chose (v1 sends the selected provider's ids); the directory's active one when omitted. */
    provider?: ProviderRef;
  }): Promise<RedeemOutcome>;
  /**
   * The OTP the partner sent. `providerId` is the WINNING provider's, from the request's answer. The
   * backend writes the points line onto the sale it answers. `directory` is optional: it names the
   * partner (`metaId`, which v1 sends), so the answer can say whether the customer's record went stale.
   */
  verifyRedeem(input: {
    sale: Sale;
    points: number;
    amount: number;
    otp: string;
    providerId: string;
    collectibleName?: string;
    /** The provider's `metaId`; read from `directory` by `providerId` when omitted. */
    metaId?: string;
    directory?: PartnerDirectory;
  }): Promise<PointsVerifyOutcome>;
  /** Give back the points on the sale's own points line (the backend reads them off the line, not from here). */
  refundPoints(input: { sale: Sale; providerId: string; directory?: PartnerDirectory }): Promise<RefundOutcome>;
  /**
   * Cancel on the redeem OTP prompt (decision 53): ask a partner that HOLDS points at request time to
   * release the hold. ⚠️ **Not verified against a device (decision 34).** The backend has no route that
   * unblocks by `blockId` alone; `POST /sale/customer/:type/release` calls the partner's refund, which
   * reads `blockId` from the sale's points line. So the sale is sent with the points line as the verify
   * WOULD have written it, and `directory` supplies the provider's `meta.discountCode` for it.
   */
  releaseHold(input: {
    sale: Sale;
    metaId: string | undefined;
    providerId: string | undefined;
    blockId: string | undefined;
    points: number;
    amount: number;
    directory: PartnerDirectory;
  }): Promise<HoldReleaseOutcome>;

  /* ── consent, programme and a typed code's OTP ─────────────────────────────────────────────── */

  /**
   * Ask the partner to send the customer the programme's consent OTP (or to say the customer already
   * consented). Sent as v1: `metaId`, and `customerInfo` when given (the OTP-less enrolment); the backend
   * reads `phoneNumber` and `providerId` alone and may forward the rest to the partner.
   */
  sendConsentOtp(input: { phoneNumber: string; providerId: string; metaId?: string; customerInfo?: unknown }): Promise<ConsentOtpOutcome>;
  /** Check the OTP and enrol the customer. `customerInfo` is required: its `contactInfo.name.firstName` is what the partner registers. */
  verifyConsent(input: { phoneNumber: string; providerId: string; metaId?: string; otp: string; customerInfo: unknown }): Promise<ConsentVerifyOutcome>;
  /**
   * Withdraw the customer from the programme. `partner` is the `metaId` of the branch's FIRST loyalty
   * provider (the backend opts out of that one, whatever the channel); a partner without `optsOut` is
   * `not-offered` and nothing is sent (decision 54). `reason` is sent when given.
   */
  optOut(input: { customerPhone: string; reason?: string; partner: string | undefined }): Promise<OptOutOutcome>;
  /** The loyalty programme of a channel (always DotPe's: the backend asks no one else). */
  programme(input: { channelDefId: string }): Promise<ProgrammeOutcome>;
  /**
   * Redeem a typed code with the OTP the customer was sent (decision 69; the old FE's prompt for a partner
   * with `codeNeedsOtp`). `sale` is required by the backend (it reads the channel off it); `requestId` is
   * the code's own, from its entry (EasyRewardz sends it back to its partner); `branchId` is the ORDER's
   * (the old FE sent it in a Support session). `partner` is the code's issuer (`CheckedCoupon.issuer`).
   */
  redeemCode(input: { code: string; partner: string; otp: string; customerPhone: string; sale: Sale; requestId?: string; branchId?: string }): Promise<CodeRedeemOutcome>;
  /**
   * Send the typed code's OTP again. The backend takes the provider DOCUMENT (it reads its `apiUserName` and
   * `storeCode`), so `directory` supplies the partner's own; the partner's `resendsCodeOtp` says whether to offer it.
   */
  resendCodeOtp(input: {
    code: string;
    partner: string;
    customerPhone: string;
    sale: Sale;
    directory: PartnerDirectory;
    requestId?: string;
    branchId?: string;
  }): Promise<CodeResendOutcome>;
}

/** The routes the port calls. */
export const PARTNER_PORT_ROUTES = [
  SALE.customerOffers,
  COUPON.verify,
  SALE.customerLoyaltyRedeem,
  SALE.customerOfferRedeem,
  SALE.customerOfferUnblock,
  SALE.customerLoyalty,
  SALE.customerLoyaltyVerify,
  SALE.customerLoyaltyRefund,
  SALE.customerPointsRelease,
  CUSTOMER.consentOtp,
  CUSTOMER.consentOtpVerify,
  CUSTOMER.optOut,
  PROGRAM.fetch,
  COUPON.redeem,
  COUPON.resend,
] as const;

/** The route a partner's OTP verify goes to (its `verifyChannel`). */
export const verifyRouteOf = (partner: Partner): Route<any, any, any> =>
  partner.verifyChannel === "loyalty-redeem" ? SALE.customerLoyaltyRedeem : SALE.customerOfferRedeem;

/**
 * The routes applying one of this partner's offers may call: the apply, and the verify when the partner
 * asks for an OTP. Decision 8 reads it: an offer whose routes the session may not call is not listed.
 */
export const applyRoutesOf = (partner: Partner): Route<any, any, any>[] =>
  partner.requiresOtpOnApply ? [SALE.customerLoyaltyRedeem, verifyRouteOf(partner)] : [SALE.customerLoyaltyRedeem];

/* ── reading the client's result ───────────────────────────────────────────────────────────── */

const isRecord = (value: unknown): value is Record<string, any> => !!value && typeof value === "object" && !Array.isArray(value);

/** A sentence the backend sent; `message` is an object or an array on some refusals, which is not one. */
const sentenceOf = (body: unknown): string | undefined => {
  const message = isRecord(body) ? body.message : undefined;
  return typeof message === "string" && message.trim() ? message : undefined;
};

/** What the client's error says, in the port's words. */
export function refusalOf(error: ApiError): Refusal {
  switch (error.kind) {
    case "refused":
      return { unanswered: "not-allowed" };
    case "session":
      return { code: error.code, unanswered: "signed-out" };
    case "offline":
      return { unanswered: "offline" };
    case "restricted":
    case "rejected":
      return { code: error.code, message: sentenceOf(error.body) };
    default:
      return { unanswered: "unreachable" };
  }
}


/** `REST_RESTRICTED_ACTION`: the customer declined a partner's consent (`sale_loyalty_service.js` 62). */
const CONSENT_REFUSED = "REST_RESTRICTED_ACTION";

const stringOf = (value: unknown): string | undefined => (typeof value === "string" && value ? value : undefined);

/** A figure the partner sent, as a number (some send it as a numeric string); anything else is not a figure. */
const numberOf = (value: unknown): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return undefined;
};

/** The `metaId` of the configured provider with this document id. */
const metaIdOfProvider = (providerId: string, directory: PartnerDirectory | undefined): string | undefined =>
  directory?.providers.find((provider) => provider.mid === providerId)?.metaId;

/**
 * The partners' balance answers, read once (points-conversations.md §2). Every adapter answers a subset of
 * `redeemablePoints`, `redeemableAmount`, `currentPoints`, `loyaltyTier`, `loyaltyOptIn`; Reelo alone answers
 * a lone `minimumAmount` (a bill below its minimum) or `-1` for all three (not redeemable today), passed through.
 * An answer with none of them is nothing (decision 52); so is DotPe's `{ visitHistory: [] }`.
 */
function readBalance(data: unknown): BalanceOutcome {
  // F6: DotPe answers the bare string "ERR_INVALID_BRAND" as a success when the channel has no brand
  if (typeof data === "string" && data.trim()) return { kind: "refused", code: data.trim() };
  if (!isRecord(data)) return { kind: "unanswered" };

  const redeemablePoints = numberOf(data.redeemablePoints);
  const redeemableAmount = numberOf(data.redeemableAmount);
  const currentPoints = numberOf(data.currentPoints);
  const minimumAmount = numberOf(data.minimumAmount);
  const loyaltyTier = stringOf(data.loyaltyTier);
  const optedIn = typeof data.loyaltyOptIn === "boolean" ? data.loyaltyOptIn : undefined;
  const hasFigure = [redeemablePoints, redeemableAmount, currentPoints, minimumAmount].some((figure) => figure !== undefined);
  if (!hasFigure && optedIn === undefined && !loyaltyTier) return { kind: "unanswered" };

  const hasRedeemable = redeemablePoints !== undefined || redeemableAmount !== undefined;
  const negative = [redeemablePoints, redeemableAmount, currentPoints].some((figure) => figure !== undefined && figure < 0);
  const note: BalanceNote | undefined = negative ? "negative-figures" : minimumAmount !== undefined && !hasRedeemable ? "minimum-amount-only" : undefined;
  return {
    kind: "balance",
    redeemablePoints: redeemablePoints ?? 0,
    redeemableAmount: redeemableAmount ?? 0,
    ...(currentPoints !== undefined ? { currentPoints } : {}),
    ...(loyaltyTier ? { loyaltyTier } : {}),
    ...(optedIn !== undefined ? { optedIn } : {}),
    ...(minimumAmount !== undefined ? { minimumAmount } : {}),
    ...(note ? { note } : {}),
  };
}

/** The backend's consent messages (`sale_customer_service.js` 705-710; v1 read the second too). */
const CONSENT_ALREADY_CONSENTED = "CUSTOMER_ALREADY_CONSENTED";
const CONSENT_CUSTOMER_EXISTS = "Customer Already Exists";

/** What `/customer/consent-otp` answered: `{ status, message, otpSent, isNewEnrollment }`. */
function readConsentOtp(data: unknown): ConsentOtpOutcome {
  const answer: Record<string, any> = isRecord(data) ? data : {};
  const message = stringOf(answer.message);
  const isNewEnrollment = typeof answer.isNewEnrollment === "boolean" ? answer.isNewEnrollment : undefined;
  if (answer.status && message === CONSENT_ALREADY_CONSENTED) return { kind: "already-consented", customerRecordStale: true };
  if (answer.status) {
    // the backend defaults `otpSent` to true, so only a partner that says false enrolled the customer with no code
    return answer.otpSent === false
      ? { kind: "enrolled-without-otp", customerRecordStale: true, ...(isNewEnrollment !== undefined ? { isNewEnrollment } : {}) }
      : { kind: "otp-sent", ...(isNewEnrollment !== undefined ? { isNewEnrollment } : {}) };
  }
  if (message === CONSENT_CUSTOMER_EXISTS) return { kind: "already-consented", customerRecordStale: true };
  return { kind: "failed", ...(message ? { message } : {}) };
}

/** What `/customer/consent-otp/verify` answered: `{ status, message, data }`. */
function readConsentVerify(data: unknown): ConsentVerifyOutcome {
  const answer: Record<string, any> = isRecord(data) ? data : {};
  const message = stringOf(answer.message);
  if (answer.status) return { kind: "verified", alreadyConsented: message === CONSENT_ALREADY_CONSENTED, customerRecordStale: true };
  return { kind: "failed", ...(message ? { message } : {}) };
}

type Call = ApiClient["call"];

/* ── the port, over a client ───────────────────────────────────────────────────────────────── */

export function createPartnerPort({ call }: { call: Call }): PartnerPort {
  return {
    routes: PARTNER_PORT_ROUTES,

    async listOffers({ sale, directory }) {
      const active = directory.active;
      // The backend needs a provider to name (its consent check); a merchant without one has no partner to list.
      if (!active?.mid) return { kind: "listed", offers: [] };
      const result = await call(SALE.customerOffers, { body: { providerId: active.mid, metaId: active.metaId, sale } as never });
      if (!result.ok) return { kind: "refused", ...refusalOf(result.error) };
      const listed: unknown = isRecord(result.data) ? result.data.discounts : undefined;
      const offers = (Array.isArray(listed) ? listed : []).filter(isRecord).map((offer): ListedOffer => {
        const partner = partnerOf(offer, directory);
        return { offer: offer as DiscountOffer, partner, key: partnerOfferKey(partner, offer as DiscountOffer) };
      });
      return { kind: "listed", offers };
    },

    async checkCode({ code, sale, customerPhone, branchId, ristaDiscount }) {
      const body: Record<string, unknown> = { couponCode: code, sale };
      // An empty phone is refused for a customer-bound coupon; a sale may have no customer (v1 deletes it too).
      if (customerPhone) body.customerPhone = customerPhone;
      if (branchId) body.branchId = branchId;
      if (ristaDiscount !== undefined) body.ristaDiscount = ristaDiscount;
      const result = await call(COUPON.verify, { body });
      if (!result.ok) return { kind: "refused", ...refusalOf(result.error) };
      return readCheckedCoupon(result.data);
    },

    async requestApply({ offer, sale, directory, code }) {
      const metaId = partnerOf(offer, directory);
      const partner = getPartner(metaId);
      const body = partner.steps.buildApplyRequest(offer, {
        sale,
        providerId: providerIdOf(metaId, directory),
        metaId,
        appliedDiscountInfo: code ? { couponCode: code } : undefined,
      });
      const result = await call(SALE.customerLoyaltyRedeem, { body: body as never });
      if (!result.ok) return { kind: "failed", partner: metaId, ...refusalOf(result.error) };

      const plan = partner.steps.readApplyAnswer(result.data, offer);
      if (plan.kind === "failed") {
        // the route's own failure after it saved the sale: `{ errorCode, sale, message }` (partner-answers.md §1)
        const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
        return { kind: "failed", partner: metaId, code: plan.code, message: sentenceOf(answer), sale: answer.sale };
      }
      return { ...plan, partner: metaId, customerRecordStale: partner.refreshesCustomerRecord };
    },

    async verify({ offer, otp, sale, directory, referenceId, code }) {
      const metaId = partnerOf(offer, directory);
      const partner = getPartner(metaId);
      const { channel, body } = partner.steps.buildVerifyRequest(offer, otp, {
        sale,
        providerId: providerIdOf(metaId, directory),
        metaId,
        referenceId,
        appliedDiscountInfo: code ? { couponCode: code } : undefined,
      });
      const route = channel === "loyalty-redeem" ? SALE.customerLoyaltyRedeem : SALE.customerOfferRedeem;
      const result = await call(route, { body: body as never });
      if (!result.ok) return { kind: "failed", partner: metaId, ...refusalOf(result.error) };

      const outcome = partner.steps.readVerifyAnswer(result.data);
      if (outcome.kind === "failed") {
        // the loyalty route's failure envelope names its code `errorCode`, not `code` (sale_loyalty_service.js 238-256)
        const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
        return { kind: "failed", partner: metaId, code: outcome.code ?? answer.errorCode, message: outcome.message };
      }
      return { ...outcome, partner: metaId, customerRecordStale: partner.refreshesCustomerRecord };
    },

    async release({ entry, sale, directory }) {
      const metaId = partnerOf(entry, directory);
      const partner = getPartner(metaId);
      const plan = partner.steps.planRelease({
        referenceId: (entry as Record<string, any>).referenceId || undefined,
        providerId: providerIdOf(metaId, directory),
        metaId,
        couponCode: listedCode(entry) ?? undefined,
        customerPhone: (sale as Record<string, any>)?.customerInfo?.contactInfo?.phoneNumber || "",
        // the partner's correlation id, when it issued one (EasyRewardz unblocks by it; decision 40)
        ...((entry as Record<string, any>).requestId ? { requestId: (entry as Record<string, any>).requestId } : {}),
        sale,
      });
      if (!plan.ok) return { kind: "skipped", partner: metaId, missing: plan.missing };

      const result = await call(SALE.customerOfferUnblock, { body: plan.body as never });
      if (!result.ok) return { kind: "failed", partner: metaId, ...refusalOf(result.error) };
      return { kind: "released", partner: metaId, customerRecordStale: partner.refreshesCustomerRecord };
    },

    /* ── points ─────────────────────────────────────────────────────────────────────────────── */

    async pointsBalance({ sale, providerId, metaId, fetch }) {
      // `metaId` and `fetch` are sent as v1: the backend may forward them to the partner
      const body: Record<string, unknown> = { sale, providerId, metaId };
      if (fetch) body.fetch = fetch;
      const result = await call(SALE.customerLoyalty, { body: body as never });
      if (!result.ok) {
        // the consent refusal is `{ code: "REST_RESTRICTED_ACTION" }` (sale_loyalty_service.js 62)
        if ((result.error.kind === "rejected" || result.error.kind === "restricted") && result.error.code === CONSENT_REFUSED) {
          return { kind: "consent-required" };
        }
        return { kind: "refused", ...refusalOf(result.error) };
      }
      return readBalance(result.data);
    },

    async requestRedeem({ sale, points, amount, directory, provider = directory.active }) {
      // sent as v1: the backend may forward it to the partner. The controller reads `providerId` and then ignores it
      // (the backend asks every provider of the branch itself, F2), and never reads `metaId`.
      const body = { loyaltyInput: { redeemablePoints: points, redeemableAmount: amount }, providerId: provider?.mid, metaId: provider?.metaId, sale };
      const result = await call(SALE.customerLoyaltyRedeem, { body: body as never });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };

      const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
      // decision 50: a `code`, an `errorCode` or no sale is a failure (the failure envelope is `{ errorCode, sale, message }`)
      if (typeof answer.errorCode === "string" || !isRecord(answer.sale)) {
        return { kind: "failed", code: stringOf(answer.errorCode), message: sentenceOf(answer), sale: isRecord(answer.sale) ? (answer.sale as Sale) : undefined };
      }
      const info: Record<string, any> = isRecord(answer.redemptionInfo) ? answer.redemptionInfo : {};
      // the winner names itself; an answer that does not is read as the merchant's active provider's
      const partner = stringOf(answer.provider) ?? directory.active?.metaId;
      const requested: RedeemRequested = {
        sale: answer.sale as Sale,
        blockId: stringOf(info.blockId) ?? (isRecord(info.data) ? stringOf(info.data.blockId) : undefined),
        partner,
        providerId: stringOf(answer.providerId) ?? providerIdOf(partner, directory),
        redeemablePoints: numberOf(info.redeemablePoints),
        redeemableAmount: numberOf(info.redeemableAmount),
        maxPointsToRedeem: numberOf(info.maxPointsToRedeem),
      };
      return { kind: info.authenticate === true ? "needs-otp" : "answered-without-otp", ...requested };
    },

    async verifyRedeem({ sale, points, amount, otp, providerId, collectibleName, metaId, directory }) {
      const partner = metaId ?? metaIdOfProvider(providerId, directory);
      const loyaltyInput: Record<string, unknown> = { redeemablePoints: points, redeemableAmount: amount, otp };
      if (collectibleName) loyaltyInput.collectibleName = collectibleName;
      // `metaId` is sent as v1: the backend may forward it to the partner (the controller never reads it)
      const result = await call(SALE.customerLoyaltyVerify, { body: { sale, providerId, metaId: partner, loyaltyInput } as never });
      if (!result.ok) {
        if ((result.error.kind === "rejected" || result.error.kind === "restricted") && result.error.code === CONSENT_REFUSED) return { kind: "consent-required" };
        return { kind: "failed", ...refusalOf(result.error) };
      }
      const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
      // decision 50, as the redeem: a `code`, an `errorCode` or no sale is a failure. The verify's `message` is the partner's raw log, a sentence only sometimes.
      if (typeof answer.errorCode === "string" || !isRecord(answer.sale)) return { kind: "failed", code: stringOf(answer.errorCode), message: sentenceOf(answer) };
      return { kind: "verified", sale: answer.sale as Sale, partner, customerRecordStale: partner ? getPartner(partner).refreshesCustomerRecord : false };
    },

    async refundPoints({ sale, providerId, directory }) {
      const result = await call(SALE.customerLoyaltyRefund, { body: { sale, providerId } as never });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
      const answeredSale = isRecord(answer.sale) ? (answer.sale as Sale) : undefined;
      if (answer.refundSuccess !== true) return { kind: "not-refunded", sale: answeredSale, code: stringOf(answer.errorCode) };
      const partner = metaIdOfProvider(providerId, directory);
      return {
        kind: "refunded",
        sale: answeredSale,
        refundedPoints: numberOf(answer.refundedPoints),
        partner,
        customerRecordStale: partner ? getPartner(partner).refreshesCustomerRecord : false,
      };
    },

    async releaseHold({ sale, metaId, providerId, blockId, points, amount, directory }) {
      const partner = getPartner(metaId);
      // only a partner that holds points when asked has a hold to release; the others took nothing at the request
      if (!metaId || !partner.holdsPointsOnRequest || !blockId) return { kind: "not-needed" };
      const discountCode = directory.providers.find((provider) => provider.metaId === metaId)?.meta?.discountCode;
      // ⚠️ QUESTION (decision 34), to verify on a device: the backend has no unblock by `blockId`; this route
      // calls the partner's refund, which reads `blockId` from the sale's points line (DotPe 75-80, 105-112).
      // So the line is sent as `verifyRedeem` would write it (points-conversations.md §3). The body's `type`
      // is what DotPe's refund reads (`type !== "loyalty"` keeps a coupon on the sale out of it); the path's
      // `:type` is never read, and is sent as the same word.
      const line = {
        discountCode,
        discountCodeDisplayName: discountCode,
        dclass: "Discount",
        mode: "Manual",
        discountType: "Absolute",
        discountRate: amount,
        amount: -amount,
        loyaltyPoints: points,
        isOverridden: true,
        posted: true,
        blockId,
      };
      // an earlier line of this partner's is replaced; with no code to match by, nothing else is touched
      const kept = ((sale as Record<string, any>).billDiscountList ?? []).filter(
        (entry: Record<string, any>) => !discountCode || entry?.discountCode !== discountCode,
      );
      const body = { sale: { ...sale, billDiscountList: [...kept, line] }, loyaltyProvider: { metaId }, type: "loyalty" };
      const result = await call(SALE.customerPointsRelease, { params: { type: "loyalty" }, body: body as never });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      const answer: Record<string, any> = isRecord(result.data) ? result.data : {};
      if (answer.refundSuccess === false) return { kind: "failed", code: stringOf(answer.errorCode) };
      return { kind: "released", partner: metaId, customerRecordStale: partner.refreshesCustomerRecord };
    },

    /* ── consent, programme and a typed code's OTP ─────────────────────────────────────────── */

    async sendConsentOtp({ phoneNumber, providerId, metaId, customerInfo }) {
      // `metaId` and `customerInfo` are sent as v1: the backend may forward them to the partner (the controller reads
      // `phoneNumber` and `providerId` alone, and asks the partner for an OTP whatever `customerInfo` says)
      const body = { phoneNumber, providerId, metaId, ...(customerInfo ? { customerInfo } : {}) };
      const result = await call(CUSTOMER.consentOtp, { body });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      return readConsentOtp(result.data);
    },

    async verifyConsent({ phoneNumber, providerId, metaId, otp, customerInfo }) {
      // `metaId` is sent as v1: the backend may forward it to the partner (the controller never reads it)
      const result = await call(CUSTOMER.consentOtpVerify, { body: { phoneNumber, otp, providerId, metaId, customerInfo } });
      if (!result.ok) return { kind: "refused", ...refusalOf(result.error) };
      return readConsentVerify(result.data);
    },

    async optOut({ customerPhone, reason, partner }) {
      // decision 54: every other partner's opt-out is an EMPTY SUCCESS that calls no one, which is not an opt-out
      if (!getPartner(partner).optsOut) return { kind: "not-offered" };
      const result = await call(CUSTOMER.optOut, { body: { customerPhone, ...(reason ? { reason } : {}) } });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      // v1 read `res.status`: the partner's own body says whether it opted the customer out
      if (!isRecord(result.data) || !result.data.status) return { kind: "failed" };
      return { kind: "opted-out", partner, customerRecordStale: getPartner(partner).refreshesCustomerRecord };
    },

    async programme({ channelDefId }) {
      const result = await call(PROGRAM.fetch, { query: { channelDefId } });
      if (!result.ok) return { kind: "refused", ...refusalOf(result.error) };
      const body: Record<string, any> = isRecord(result.data) ? result.data : {};
      if (body.status) return { kind: "programme", programme: body };
      return { kind: "none", ...(body.profileConfig ? { profileConfig: body.profileConfig } : {}) };
    },

    async redeemCode({ code, partner, otp, customerPhone, sale, requestId, branchId }) {
      // the old FE's body (SaleService.js 3927-3945): every field is sent as it did, the backend may forward them to the partner
      const body = { provider: partner, couponCode: code, customerPhone, otp, sale, ...(requestId ? { requestId } : {}), ...(branchId ? { branchId } : {}) };
      const result = await call(COUPON.redeem, { body });
      const release = getPartner(partner).releasesCouponOnWrongOtp;
      if (!result.ok) {
        // the backend answered the partner's refusal as a code: the old FE read every one as a wrong OTP (and for
        // Exclusife it is the only signal there is). A feature refusal is not one.
        if (result.error.kind === "rejected") return { kind: "wrong-otp", partner, release, ...refusalOf(result.error) };
        return { kind: "failed", partner, ...refusalOf(result.error) };
      }
      // an answer with nothing in it is no redemption (the old FE added the coupon on it)
      if (!isRecord(result.data)) return { kind: "failed", partner };
      const answer: Record<string, any> = result.data;
      // `status: false` (EasyRewardz answers it with the partner's `message`, no `error`) or an `error` (DotPe's body)
      if (answer.status === false || answer.error) {
        return { kind: "wrong-otp", partner, release, code: stringOf(answer.error), message: sentenceOf(answer) };
      }
      // Exclusife answers `status: "SUCCESS"`, EasyRewardz and DotPe `status: true`; nothing here is a refusal
      return { kind: "redeemed", partner, confirmed: answer.status === true };
    },

    async resendCodeOtp({ code, partner, customerPhone, sale, directory, requestId, branchId }) {
      // the backend reads the provider DOCUMENT (`apiUserName`, `storeCode`), as the old FE sent it (ItemEditController.js 1411)
      const provider = directory.providers.find((candidate) => candidate.metaId === partner) ?? directory.active;
      // not sent: the backend would refuse a call with no provider; its own code for it
      if (!provider) return { kind: "failed", code: "REST_INVALID_LOYALTY_PROVIDER" };
      const body = { provider, couponCode: code, customerPhone, sale, type: "Coupon", ...(requestId ? { requestId } : {}), ...(branchId ? { branchId } : {}) };
      const result = await call(COUPON.resend, { body });
      if (!result.ok) return { kind: "failed", ...refusalOf(result.error) };
      return { kind: "sent" };
    },
  };
}

/**
 * The partner a checked coupon's issuer names. Partners stamp their `metaId` (DotPe, Xeno, Blue Tokai,
 * EasyRewardz), but Snaplion stamps its display name, `SnapLion` (api_snaplion_service.js 413-428,
 * properties.json 2168-2169), so the match ignores case; an issuer no folder knows is kept as it is.
 */
function partnerNamed(issuer: string | undefined): string | null {
  if (!issuer) return null;
  if (PARTNERS[issuer]) return issuer;
  const lower = issuer.toLowerCase();
  return PARTNERS[lower] ? lower : issuer;
}

/** The code check's answer: the coupon when it is `Active` (as v1's saga required), else why not. */
function readCheckedCoupon(data: unknown): CodeOutcome {
  if (!isRecord(data)) return { kind: "refused", unanswered: "unreachable" };
  if (data.alreadyApplied === true) return { kind: "already-applied" };
  if (data.status !== "Active") return { kind: "refused", status: typeof data.status === "string" ? data.status : undefined };

  const discounts = (Array.isArray(data.discounts) ? data.discounts : []).filter(isRecord) as DiscountOffer[];
  const issuer: string | undefined = isRecord(data.sourceInfo) && typeof data.sourceInfo.provider === "string" ? data.sourceInfo.provider : undefined;
  // The backend marks each discount a partner's coupon releases `isExternal: "true"` (coupon_service.js 860-863);
  // a coupon of the merchant's own has an issuer too (its coupon provider's id), but no partner.
  const fromPartner = discounts.some((discount) => String(discount.isExternal) === "true");
  return {
    kind: "found",
    coupon: {
      code: String(data.couponCode ?? ""),
      issuer,
      partner: fromPartner ? partnerNamed(issuer) : null,
      discounts,
      requestId: typeof data.requestId === "string" ? data.requestId : undefined,
      referenceId: typeof data.referenceId === "string" ? data.referenceId : undefined,
      otpRequired: data.OTPRequired === true || data.otpRequired === true,
      answer: data,
    },
  };
}
